/**
 * Carrying the bank's own reason to `/payment-error` - `FRONTEND.md` §9.470.
 *
 * **Why the reason travels at all.** §10A.166 exists so that an open breaker, a refused token exchange,
 * a refused create and an answer with no URL are four different sentences rather than one; a checkout
 * that folded them into *Objednávku sa nepodarilo odoslať* threw that away **and** told the customer
 * something false, because the order had already been placed.
 *
 * **In the URL rather than in state**, because the landing paths are bare - `LandingPages`'s own comment
 * records that *"the landing paths carry no order reference"* and that is why the checkout remembers the
 * order in this tab before the browser goes anywhere. A reason in router state would be lost by the one
 * thing a customer on a failed payment is most likely to do, which is reload.
 */

/** The page's own wording already covers the general case, so a blank reason adds no parameter. */
const PATH = '/payment-error'

/**
 * How much of a server sentence is carried.
 *
 * <p>Long enough for every message §10A.166 can produce, short enough that a stack trace or an HTML
 * error page leaking into `error` cannot build a URL a proxy will refuse. Truncation is silent: the page
 * below the reason says what to do either way, so a clipped sentence is still better than none.
 */
const MAX_REASON = 160

/** Where to send a customer whose order was placed and whose card session was not. */
export function paymentErrorPath(reason?: string | null): string {
  const trimmed = (reason ?? '').trim().slice(0, MAX_REASON)
  return trimmed === '' ? PATH : `${PATH}?reason=${encodeURIComponent(trimmed)}`
}

/**
 * The reason back out of a query string, or an empty string.
 *
 * <p>Takes the search string rather than reading `window.location` so it can be driven directly.
 *
 * <p><b>A malformed escape is answered as no reason, and finding out how needed running it.</b> The
 * first version of this guarded a `try/catch` on the assumption that `URLSearchParams` throws on a bad
 * percent sequence. It does not - it substitutes <b>U+FFFD</b> and hands back the rest, so
 * {@code ?reason=%E0%A4%A} reads as {@code "�%A"}. A replacement character printed under *your
 * order was received* makes a correct page look broken, which is worse than saying nothing, so a value
 * carrying one is discarded.
 *
 * <p>The `try/catch` is kept for the construction itself and is <b>not</b> what handles that case; the
 * test says which is which, because a guard credited with work it does not do is the shape of
 * {@code agos-a-check-that-passes-on-nothing}.
 */
export function paymentErrorReason(search: string): string {
  try {
    const value = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('reason')
    const trimmed = (value ?? '').trim()
    if (trimmed.includes('\uFFFD')) return ''
    return trimmed.slice(0, MAX_REASON)
  } catch {
    return ''
  }
}
